import { useTranslation } from 'react-i18next';

import { appConfig } from '@/app/config/appConfig';
import type {
  StageContentPage,
} from '@/types/stage-content-api.types';
import type {
  InstructionItem,
  InstructionSlide,
  StageContent,
  StageInstructionContentMap,
} from '@micro-screens/src/types/instruction-content';
import type { StageId } from '@micro-screens/src/types/stage-theme';

import gameMetadata from '../../game/data/game-metadata.json';

function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function mapPage(page: StageContentPage): InstructionSlide {
  const variant =
    page.point_type === 'ORDERED' ? ('step' as const) : ('rule' as const);
  const items: InstructionItem[] = page.points.map((point) => ({
    id: slugify(point.title),
    title: point.title,
    description: point.description,
    variant,
  }));
  return {
    id: slugify(page.title),
    title: page.title,
    description: page.description,
    items,
  };
}

function buildContentByStage(
  stageNumber: StageId,
  content: Partial<StageContent>,
): Partial<StageInstructionContentMap> {
  const result: Partial<StageInstructionContentMap> = {};
  result[stageNumber] = content as StageContent;
  return result;
}

export interface UseStageContentResult {
  contentByStage: Partial<StageInstructionContentMap> | undefined;
  isLoading: boolean;
}

/**
 * Provides stage content for the instructions screen.
 * Reads directly from the bundled game-metadata.json — no API call needed.
 */
export function useStageContent(_stageId: string): UseStageContentResult {
  const { i18n } = useTranslation();
  const stageNumber = appConfig.game.stageNo as StageId;
  const lang = i18n.language.toUpperCase() === 'HI' ? 'HI' : 'EN';

  // Find the matching language section from the bundled metadata
  const stageContent = gameMetadata.stage_content.find(
    (sc) => sc.language === lang,
  );

  if (!stageContent) {
    // Fallback to English if the requested language isn't found
    const fallback = gameMetadata.stage_content.find(
      (sc) => sc.language === 'EN',
    );
    if (!fallback) {
      return { contentByStage: undefined, isLoading: false };
    }
    return buildResult(fallback, stageNumber);
  }

  return buildResult(stageContent, stageNumber);
}

function buildResult(
  stageContent: (typeof gameMetadata.stage_content)[number],
  stageNumber: StageId,
): UseStageContentResult {
  const pages = stageContent.content.pages.filter(
    (p) => p.visible_in_app,
  );
  if (pages.length === 0) {
    return { contentByStage: undefined, isLoading: false };
  }

  const slides = pages.map((p) => mapPage(p as unknown as StageContentPage));
  const content: Partial<StageContent> = {
    ...(stageContent.content.play_now_button
      ? { playNowButton: stageContent.content.play_now_button }
      : {}),
    ...(stageContent.content.learn_how_to_play
      ? { learnHowToPlay: stageContent.content.learn_how_to_play }
      : {}),
    slides,
  };

  return {
    contentByStage: buildContentByStage(stageNumber, content),
    isLoading: false,
  };
}
